import { formatTime } from "@utils";
import dayjs from "dayjs";
import { EChartsOption } from "echarts";
import { t } from "i18n";
import { compact } from "lodash-es";
import { greenColor, redColor } from "src/common/constants";

import { formatAmount } from "../../utils";
import { CalculateDataProps } from "./calculate.worker";

type GenerateOptionsType = (params: CalculateDataProps) => EChartsOption;
/** 生成 echarts 配置项 */
export const generateOptions: GenerateOptionsType = (params) => {
  const {
    endPointIndex,
    inflationList,
    markPointIndex,
    maxSurplusDate,
    maxSurplusValue,
    minSurplusDate,
    minSurplusValue,
    originList,
    prepaymentList,
    profitList,
    surplusList,
    totalList,
    xAxisData,
  } = params;

  const options: EChartsOption = {
    animation: false,
    grid: { left: "5%", right: "5%" },
    legend: {
      data: [
        t("asset_dashboard.chart_total_amount"),
        t("asset_dashboard.chart_profit"),
        t("asset_dashboard.chart_net_deposit"),
        t("asset_dashboard.chart_net_deposit_inflation"),
        t("asset_dashboard.chart_prepayment"),
        t("asset_dashboard.chart_remain_amount"),
      ],
      selected: {
        // "剩余金额": true,
        // 存款净额: false,
        [t("asset_dashboard.chart_net_deposit_inflation")]: false,
        // "总额": false,
        // 预付款: false,
      },
      textStyle: {
        color: "white",
      },
    },
    series: [
      {
        data: totalList.map((item) => item.toFixed(2)),
        lineStyle: { opacity: 0.6 },
        name: t("asset_dashboard.chart_total_amount"),
        symbol: "none",
        type: "line",
      },
      {
        data: profitList.map((item) => item.toFixed(2)),
        lineStyle: { opacity: 0.6 },
        name: t("asset_dashboard.chart_profit"),
        symbol: "none",
        type: "line",
      },
      {
        data: originList.map((item) => item.toFixed(2)),
        lineStyle: { opacity: 0.6 },
        name: t("asset_dashboard.chart_net_deposit"),
        symbol: "none",
        type: "line",
      },
      {
        data: inflationList.map((item) => item.toFixed(2)),
        lineStyle: { opacity: 0.6 },
        name: t("asset_dashboard.chart_net_deposit_inflation"),
        symbol: "none",
        type: "line",
      },
      {
        data: prepaymentList.map((item) => item.toFixed(2)),
        lineStyle: { opacity: 0.6 },
        name: t("asset_dashboard.chart_prepayment"),
        symbol: "none",
        type: "line",
      },
      {
        data: surplusList.map((item) => item.toFixed(2)),
        lineStyle: { width: 5 },
        markLine: {
          data: [
            {
              label: {
                formatter: `${t("asset_dashboard.chart_remain_amount")} ${t("asset_dashboard.chart_min_value")} ${formatAmount(minSurplusValue)}`,
                position: "insideStartBottom",
              },
              type: "min",
            },
            {
              label: {
                formatter: `${t("asset_dashboard.chart_remain_amount")} ${t("asset_dashboard.chart_max_value")} ${formatAmount(maxSurplusValue)}`,
                position: "insideStartBottom",
              },
              type: "max",
            },
            // {
            //   type: "average",
            //   label: {
            //     position: "insideStartBottom",
            //     formatter: ({ value }) =>
            //       value && `剩余金额平均值 ${formatAmount(+value)}`,
            //   },
            // },
          ],
        },
        markPoint: {
          data: compact([
            {
              label: {
                formatter: `${minSurplusDate} (${dayjs(minSurplusDate).diff(dayjs(), "y")}${t("asset_dashboard.chart_year_later")})`,
              },
              name: t("asset_dashboard.chart_min_value"),
              type: "min",
            },
            {
              label: {
                formatter: `${maxSurplusDate} (${dayjs(maxSurplusDate).diff(dayjs(), "y")}${t("asset_dashboard.chart_year_later")})`,
              },
              name: t("asset_dashboard.chart_max_value"),
              type: "max",
            },
            markPointIndex >= 0 && {
              coord: [markPointIndex, surplusList[markPointIndex]],
              itemStyle: { color: "green" },
              name: t("asset_dashboard.chart_turning_point"),
              value: `${xAxisData[markPointIndex]}(${dayjs(xAxisData[markPointIndex]).diff(dayjs(), "y")}${t("asset_dashboard.chart_year_later")})\n\n拐点（当剩余金额超过初试存款5%时）\n\n${formatAmount(surplusList[markPointIndex])}`,
            },
            endPointIndex >= 0 && {
              coord: [endPointIndex, surplusList[endPointIndex]],
              itemStyle: { color: "red" },
              name: t("asset_dashboard.chart_turning_point"),
              value: `${xAxisData[endPointIndex]}(${dayjs(xAxisData[endPointIndex]).diff(dayjs(), "y")}${t("asset_dashboard.chart_year_later")})\n\n拐点（当剩余金额低于0时）\n\n${formatAmount(surplusList[markPointIndex])}`,
            },
          ]),
        },
        name: t("asset_dashboard.chart_remain_amount"),
        symbol: "none",
        type: "line",
      },
    ],
    tooltip: {
      axisPointer: { type: "line" },
      formatter: (params: any) => {
        const curDate = formatTime(params[0].axisValue, "YYYY-MM-DD");
        const diffDateYear = dayjs(params[0].axisValue).diff(dayjs(), "y");
        const originItem = params?.find(
          ({ seriesName = "" } = {}) =>
            seriesName === t("asset_dashboard.chart_net_deposit"),
        );
        const surplusItem = params?.find(
          ({ seriesName = "" } = {}) =>
            seriesName === t("asset_dashboard.chart_remain_amount"),
        );

        const surplusColor =
          surplusItem?.value > originItem?.value ? greenColor : redColor;
        const legendData = [
          {
            color: "",
            name: `* ${t("asset_dashboard.chart_total_amount")}(存款净额+周期复利-周期预付)`,
            seriesName: t("asset_dashboard.chart_total_amount"),
          },
          {
            color: greenColor,
            name: t("asset_dashboard.chart_profit") + "(周期复利-周期预付)",
            seriesName: t("asset_dashboard.chart_profit"),
          },
          // { name: "存款净额", seriesName: "存款净额", color: "" },
          {
            color: "",
            name: t("asset_dashboard.chart_net_deposit_inflation"),
            seriesName: t("asset_dashboard.chart_net_deposit_inflation"),
          },
          {
            color: redColor,
            name: t("asset_dashboard.chart_prepayment"),
            seriesName: t("asset_dashboard.chart_prepayment"),
          },
          {
            color: surplusColor,
            name: `* ${t("asset_dashboard.chart_remain_amount")}`,
            seriesName: t("asset_dashboard.chart_remain_amount"),
          },
        ];

        const result = legendData.map(({ color, name, seriesName }) => {
          const item = params?.find((i: any) => i.seriesName === seriesName);
          const value = item?.value ?? "";
          return (
            value &&
            `<strong${color ? ` style="color:${color}"` : ""}>${name}：${formatAmount(value)}</strong><br/>
              ${`${value}`.includes(".") ? `<div>${t("asset_dashboard.chart_exact_value")}(${value})</div>` : ""}`
          );
        });
        return compact([
          `${curDate} (${diffDateYear}${t("asset_dashboard.chart_year_later")})`,
          ...result,
        ]).join("<br/>");
      },
      showDelay: 0,
      trigger: "axis",
    },
    xAxis: {
      boundaryGap: false,
      data: xAxisData,
      type: "category",
    },
    yAxis: { type: "value" },
  };
  return options;
};
